import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiShoppingBag, FiCheck, FiX, FiClock, FiRefreshCw, FiUser, FiMessageSquare, FiTag, FiFileText, FiPaperclip, FiPackage, FiTruck, FiCheckCircle, FiDownloadCloud, FiLink, FiExternalLink } from 'react-icons/fi';
import { FaUniversity, FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { merchantAPI, orderAPI, invoiceAPI } from '../../services/api';
import CreateOrderModal from '../../components/orders/CreateOrderModal';
import ContactCustomerModal from '../../components/chat/ContactCustomerModal';
import { InvoicesTable } from './Invoices';

const DLOG_LABEL = { delivered: 'Accès remis', redelivered: 'Accès renvoyé', accessed: 'Accès consulté', received: 'Réception confirmée' };

const STATUS_META = {
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
  confirmed: { label: 'Préparée', cls: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Terminée', cls: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Annulée', cls: 'bg-red-100 text-red-700' }
};
const PAY_META = {
  pending: { label: 'Non payée', cls: 'bg-gray-100 text-gray-600' },
  completed: { label: 'Payée', cls: 'bg-green-100 text-green-700' },
  failed: { label: 'Échec', cls: 'bg-red-100 text-red-700' },
  refunded: { label: 'Remboursée', cls: 'bg-purple-100 text-purple-700' }
};
const VOUCHER_META = {
  active: { label: 'Actif', cls: 'bg-green-100 text-green-700' },
  used: { label: 'Utilisé', cls: 'bg-gray-200 text-gray-700' },
  expired: { label: 'Expiré', cls: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Annulé', cls: 'bg-yellow-100 text-yellow-800' }
};

// Filtres statut commande
const STATUS_FILTERS = [
  { key: 'all', label: 'Toutes' },
  { key: 'to_process', label: 'À traiter' },
  { key: 'pending', label: 'En attente' },
  { key: 'confirmed', label: 'Préparées' },
  { key: 'completed', label: 'Terminées' },
  { key: 'cancelled', label: 'Annulées' }
];
// Filtres statut paiement
const PAY_FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'completed', label: 'Payées' },
  { key: 'pending', label: 'Non payées' }
];

export default function MerchantOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => {
    const f = new URLSearchParams(window.location.search).get('filter');
    return ['to_process', 'pending', 'confirmed', 'completed', 'cancelled', 'all'].includes(f) ? f : 'all';
  });       // statut commande
  const [payFilter, setPayFilter] = useState('all');  // statut paiement
  const [busyId, setBusyId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [contactOrder, setContactOrder] = useState(null);
  const [highlightId, setHighlightId] = useState(null);

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'invoices' ? 'invoices' : 'orders'
  );
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invLoaded, setInvLoaded] = useState(false);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'invoices') setActiveTab('invoices');
    else if (tab === 'orders') setActiveTab('orders');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Filtre piloté par l'URL (?filter=to_process depuis la pastille du menu)
  useEffect(() => {
    const f = searchParams.get('filter');
    if (f && STATUS_FILTERS.some((x) => x.key === f)) setFilter(f);
  }, [searchParams]);

  // Ouverture ciblée depuis une notification : /merchant/orders?order=<id>
  useEffect(() => {
    const oid = searchParams.get('order');
    if (!oid || !orders.length) return;
    const el = document.getElementById(`order-card-${oid}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightId(String(oid));
      const t = setTimeout(() => setHighlightId(null), 2800);
      return () => clearTimeout(t);
    }
  }, [orders, searchParams]);

  useEffect(() => {
    if (activeTab !== 'invoices' || invLoaded) return;
    setInvLoading(true);
    (async () => {
      try {
        const res = await invoiceAPI.getMine();
        setInvoices(res.data?.data || []);
      } catch (e) {
        setInvoices([]);
      } finally {
        setInvLoading(false);
        setInvLoaded(true);
      }
    })();
  }, [activeTab, invLoaded]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'to_process') {
        params.toProcess = 1; // « À traiter » : filtre composé côté serveur (= pastille du menu)
      } else {
        if (filter !== 'all') params.status = filter;
        if (payFilter !== 'all') params.paymentStatus = payFilter;
      }
      const res = await merchantAPI.getOrders(params);
      const payload = res?.data?.data ?? res?.data ?? [];
      setOrders((Array.isArray(payload) ? payload : []).map((o) => ({ ...o, customer: o.customer || ((o.guestName || o.guestPhone || o.guestEmail) ? { firstName: o.guestName, lastName: '', email: o.guestEmail, phone: o.guestPhone } : null) })));
    } catch (e) {
      console.error('Erreur chargement commandes:', e);
      toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Impossible de charger les commandes');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [filter, payFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Rafraîchit au retour sur l'onglet (ex. après que l'admin a confirmé un paiement
  // dans une autre session) -> « Marquer terminée » se débloque sans recharger.
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') fetchOrders(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [fetchOrders]);

  const changeStatus = async (order, status) => {
    const id = order.id || order._id;
    setBusyId(id);
    try {
      await orderAPI.updateStatus(id, status);
      toast.success(`Commande ${STATUS_META[status]?.label?.toLowerCase() || status}`);
      fetchOrders();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setBusyId(null);
    }
  };

  // ===== Fulfillment produits physiques (machine à états livraison) =====
  const [shipModal, setShipModal] = useState(null); // { order } pour saisir transporteur + suivi
  const [shipForm, setShipForm] = useState({ carrier: '', trackingNumber: '' });

  const doDelivery = async (order, action, extra = {}) => {
    const id = order.id || order._id;
    setBusyId(id);
    try {
      await merchantAPI.updateOrderDelivery(id, { action, ...extra });
      const labels = { accept: 'acceptée', prepare: 'préparée', ship: 'expédiée', deliver: 'livrée' };
      toast.success(`Commande ${labels[action] || 'mise à jour'}`);
      setShipModal(null);
      setShipForm({ carrier: '', trackingNumber: '' });
      fetchOrders();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de la mise à jour de la livraison');
    } finally {
      setBusyId(null);
    }
  };

  const isPhysicalOrder = (order) =>
    (order.items || []).some((it) => ((it.deal && it.deal.productType) || it.productType) === 'physical');
  const isDigitalOrder = (order) =>
    (order.items || []).some((it) => ((it.deal && it.deal.productType) || it.productType) === 'digital');

  const [digitalModal, setDigitalModal] = useState(null); // { order }
  const [digitalForm, setDigitalForm] = useState({ link: '', note: '' });

  const doDeliverDigital = async (order) => {
    const id = order.id || order._id;
    setBusyId(id);
    try {
      await merchantAPI.deliverDigital(id, { link: digitalForm.link.trim(), note: digitalForm.note.trim() });
      toast.success('Produit digital livré');
      setDigitalModal(null);
      setDigitalForm({ link: '', note: '' });
      fetchOrders();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de la livraison digitale');
    } finally {
      setBusyId(null);
    }
  };

  // LOT C : partager le lien magique de confirmation avec l'acheteur (WhatsApp / copie)
  const shareConfirm = (order) => {
    const url = order.confirmUrl;
    if (!url) { toast.error('Lien indisponible'); return; }
    const msg = `Bonjour, votre commande ${order.orderNumber} est livrée. Merci de confirmer la réception ici : ${url}`;
    const phone = String(order.guestPhone || order.shippingPhone || (order.customer && order.customer.phone) || '').replace(/\D/g, '');
    if (navigator.share) {
      navigator.share({ title: 'Confirmation de réception', text: msg }).catch(() => {});
      return;
    }
    const wa = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(wa, '_blank');
  };

  const copyConfirm = (order) => {
    if (!order.confirmUrl) return;
    try { navigator.clipboard.writeText(order.confirmUrl); toast.success('Lien copié'); } catch (e) { toast.error('Copie impossible'); }
  };

  const merchantSubtotal = (order) =>
    (order.items || []).reduce((acc, it) => acc + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);

  const customerName = (order) => {
    const c = order.customer || {};
    const n = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return n || c.email || 'Client';
  };

  // Compteurs
  const counts = {
    total: orders.length,
    paid: orders.filter((o) => o.paymentStatus === 'completed').length,
    unpaid: orders.filter((o) => o.paymentStatus === 'pending').length,
    done: orders.filter((o) => o.status === 'completed').length
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <FiShoppingBag className="text-[#e11d7b]" size={24} />
          <h1 className="text-2xl font-bold text-gray-900">Mes commandes</h1>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setShowCreate(true)} className="px-3 py-2 rounded-lg bg-[#e11d7b] text-white text-sm font-medium hover:opacity-90">+ Créer une commande</button>
          <button type="button" onClick={fetchOrders} className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">
            <FiRefreshCw size={16} /> Actualiser
          </button>
        </div>
      </div>

      {/* Onglets Commandes / Factures */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-5">
        {[
          { key: 'orders', label: 'Mes commandes', icon: FiShoppingBag },
          { key: 'invoices', label: 'Factures', icon: FiFileText }
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === t.key
                  ? 'border-[#e11d7b] text-[#e11d7b]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'invoices' ? (
        invLoading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#e11d7b]" />
          </div>
        ) : (
          <InvoicesTable invoices={invoices} />
        )
      ) : (
      <>
      {/* Compteurs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Commandes', value: counts.total },
          { label: 'Payées', value: counts.paid, cls: 'text-green-600' },
          { label: 'Non payées', value: counts.unpaid, cls: 'text-amber-600' },
          { label: 'Terminées', value: counts.done, cls: 'text-blue-600' }
        ].map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-100 p-3">
            <div className="text-xs text-gray-500">{c.label}</div>
            <div className={`text-2xl font-bold ${c.cls || 'text-gray-900'}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="space-y-2 mb-5">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${filter === f.key ? 'bg-[#e11d7b] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-gray-400 mr-1">Paiement :</span>
          {PAY_FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setPayFilter(f.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${payFilter === f.key ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#e11d7b]" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl shadow-sm">
          <FiShoppingBag className="mx-auto text-gray-300 mb-3" size={48} />
          <p className="text-gray-600">Aucune commande pour ce filtre.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const id = order.id || order._id;
            const sm = STATUS_META[order.status] || STATUS_META.pending;
            const pm = PAY_META[order.paymentStatus] || PAY_META.pending;
            const busy = busyId === id;
            const c = order.customer || {};
            const vouchers = order.vouchers || [];
            return (
              <div key={id} id={`order-card-${id}`} className={`bg-white rounded-xl shadow-sm border p-4 md:p-5 transition-all ${highlightId === String(id) ? 'ring-2 ring-[#e11d7b] border-[#e11d7b]' : 'border-gray-100'}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-gray-900">#{order.orderNumber || id}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.cls}`}>{sm.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pm.cls}`}>{pm.label}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{order.createdAt ? new Date(order.createdAt).toLocaleString('fr-FR') : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Sous-total (vos articles)</p>
                    <p className="text-lg font-bold text-[#e11d7b]">{merchantSubtotal(order).toFixed(2)} MAD</p>
                  </div>
                </div>

                {/* Client */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mb-3">
                  <span className="flex items-center gap-1"><FiUser size={14} /> {customerName(order)}</span>
                  {(c.id || c._id || order.userId) ? (
                    <button type="button" onClick={() => setContactOrder(order)}
                      className="flex items-center gap-1 text-[#e11d7b] hover:underline">
                      <FiMessageSquare size={14} /> Contacter via Maxula
                    </button>
                  ) : (
                    <span className="flex items-center gap-1 text-gray-400" title="Commande invité — aucun compte client à contacter">
                      <FiMessageSquare size={14} /> Contacter via Maxula
                    </span>
                  )}
                </div>

                {/* Articles */}
                <div className="divide-y divide-gray-100 border-t border-b border-gray-100 mb-3">
                  {(order.items || []).map((it, i) => {
                    const label = it.title || it.deal?.title || 'Article';
                    const ref = it.deal?.slug || it.deal?.id || it.dealId;
                    return (
                    <div key={i} className="flex items-center justify-between py-2 text-sm">
                      {ref ? (
                        <a href={`/deals/${ref}`} target="_blank" rel="noopener noreferrer"
                          className="text-gray-800 hover:text-primary-600 hover:underline inline-flex items-center gap-1"
                          title="Voir le produit">
                          {label}<FiExternalLink size={12} className="opacity-50 shrink-0" />
                        </a>
                      ) : (
                        <span className="text-gray-800">{label}</span>
                      )}
                      <span className="text-gray-500">
                        {it.quantity} × {(Number(it.price) || 0).toFixed(2)} =
                        {' '}<span className="font-medium text-gray-800">{((Number(it.price) || 0) * (Number(it.quantity) || 0)).toFixed(2)} MAD</span>
                      </span>
                    </div>
                    );
                  })}
                </div>

                {/* Vouchers */}
                {vouchers.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1"><FiTag size={13} /> Coupons</div>
                    <div className="flex flex-wrap gap-2">
                      {vouchers.map((v) => {
                        const vm = VOUCHER_META[v.status] || VOUCHER_META.active;
                        return (
                          <span key={v.id || v.code} className="inline-flex items-center gap-1.5 text-xs border rounded-lg px-2 py-1">
                            <span className="font-mono font-medium">{v.code}</span>
                            <span className={`px-1.5 py-0.5 rounded-full ${vm.cls}`}>{vm.label}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {order.paymentStatus !== 'completed' && order.status !== 'cancelled' &&
                    !['online', 'card', 'carte', 'payzone', 'cmi'].includes(String(order.paymentMethod || '').toLowerCase()) && (
                    <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 text-sm font-medium border border-indigo-200">
                      <FaUniversity size={14} /> Paiement — vérification par l'admin
                    </span>
                  )}
                  {order.hasPaymentProof && (
                    <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium border border-gray-200">
                      <FiPaperclip size={15} /> Reçu de virement déposé — vérifié par l'admin
                    </span>
                  )}
                  {order.status !== 'cancelled' && (
                    isDigitalOrder(order) ? (
                      <div className="w-full">
                        {order.paymentStatus !== 'completed' ? (
                          <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-gray-500 text-sm border border-gray-200 w-max">
                            <FiClock size={14} /> En attente de confirmation du paiement
                          </span>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              {order.deliveryStatus === 'received' ? (
                                <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-50 text-green-700 text-sm font-medium border border-green-200">
                                  <FiCheckCircle size={14} /> Réceptionnée par l'acheteur
                                </span>
                              ) : order.deliveryStatus === 'delivered' ? (
                                <>
                                  <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 text-amber-700 text-sm font-medium border border-amber-200">
                                    <FiDownloadCloud size={14} /> Accès remis — en attente de confirmation
                                  </span>
                                  <button type="button" disabled={busy} onClick={() => { setDigitalForm({ link: order.digitalLink || '', note: order.digitalNote || '' }); setDigitalModal({ order }); }}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
                                    <FiRefreshCw size={14} /> Renvoyer l'accès
                                  </button>
                                </>
                              ) : (
                                <button type="button" disabled={busy} onClick={() => { setDigitalForm({ link: '', note: '' }); setDigitalModal({ order }); }}
                                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50">
                                  <FiDownloadCloud size={15} /> Livrer le produit digital
                                </button>
                              )}
                            </div>
                            {order.digitalLink && (
                              <a href={order.digitalLink} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm text-violet-700 hover:underline break-all">
                                <FiLink size={13} /> {order.digitalLink}
                              </a>
                            )}
                            {Array.isArray(order.digitalLog) && order.digitalLog.length > 0 && (
                              <div className="text-xs text-gray-500 border-t pt-2 mt-1">
                                <p className="font-medium text-gray-600 mb-1">Journal de traçabilité</p>
                                {order.digitalLog.slice(-5).map((l, i) => (
                                  <div key={i} className="flex items-center gap-2">
                                    <span className="font-mono">{new Date(l.at).toLocaleString('fr-FR')}</span>
                                    <span>· {DLOG_LABEL[l.event] || l.event}</span>
                                    <span className="text-gray-400">({l.by === 'merchant' ? 'vendeur' : 'acheteur'})</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : isPhysicalOrder(order) ? (
                      <>
                        {/* Machine à états livraison — paiement confirmé requis */}
                        {order.paymentStatus === 'completed' && order.deliveryStatus !== 'received' && (
                          <>
                            {(!order.deliveryStatus || order.deliveryStatus === 'pending') && (
                              <button type="button" disabled={busy} onClick={() => doDelivery(order, 'accept')}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                                <FiCheck size={15} /> Accepter la commande
                              </button>
                            )}
                            {order.deliveryStatus === 'accepted' && (
                              <button type="button" disabled={busy} onClick={() => doDelivery(order, 'prepare')}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                                <FiPackage size={15} /> Marquer préparée
                              </button>
                            )}
                            {order.deliveryStatus === 'prepared' && (
                              <button type="button" disabled={busy} onClick={() => { setShipForm({ carrier: '', trackingNumber: '' }); setShipModal({ order }); }}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50">
                                <FiTruck size={15} /> Expédier
                              </button>
                            )}
                            {order.deliveryStatus === 'shipped' && (
                              <button type="button" disabled={busy} onClick={() => doDelivery(order, 'deliver')}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                                <FiCheckCircle size={15} /> Marquer livré
                              </button>
                            )}
                          </>
                        )}
                        {order.paymentStatus !== 'completed' && (
                          <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-gray-500 text-sm border border-gray-200">
                            <FiClock size={14} /> En attente de confirmation du paiement
                          </span>
                        )}
                        {order.deliveryStatus === 'delivered' && (
                          <div className="flex flex-col gap-1.5">
                            <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 text-amber-700 text-sm font-medium border border-amber-200">
                              <FiTruck size={14} /> Livré — en attente de confirmation de l'acheteur
                            </span>
                            {order.autoConfirmAt && (
                              <span className="text-xs text-gray-400 px-1">
                                Auto-validation le {new Date(order.autoConfirmAt).toLocaleDateString('fr-FR')}
                                {Math.ceil((new Date(order.autoConfirmAt) - new Date()) / 86400000) > 0 ? ` (dans ${Math.ceil((new Date(order.autoConfirmAt) - new Date()) / 86400000)} j)` : ''} si l'acheteur n'agit pas.
                              </span>
                            )}
                            {order.confirmUrl && (
                              <div className="flex items-center gap-2">
                                <button type="button" onClick={() => shareConfirm(order)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700">
                                  <FaWhatsapp size={13} /> Envoyer le lien de confirmation
                                </button>
                                <button type="button" onClick={() => copyConfirm(order)} title="Copier le lien"
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold hover:bg-gray-50">
                                  <FiLink size={13} /> Copier
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {order.deliveryStatus === 'received' && (
                          <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-50 text-green-700 text-sm font-medium border border-green-200">
                            <FiCheckCircle size={14} /> Réceptionnée par l'acheteur
                          </span>
                        )}
                        {(order.carrier || order.trackingNumber) && (
                          <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-50 text-gray-600 text-sm border border-gray-200">
                            <FiTruck size={14} /> {order.carrier || ''} {order.trackingNumber ? `· ${order.trackingNumber}` : ''}
                          </span>
                        )}
                        {order.status !== 'completed' && !['shipped', 'delivered', 'received'].includes(order.deliveryStatus) && (
                          <button type="button" disabled={busy}
                            onClick={() => { if (window.confirm('Annuler cette commande ? Le stock sera restauré.')) changeStatus(order, 'cancelled'); }}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 disabled:opacity-50">
                            <FiX size={15} /> Annuler
                          </button>
                        )}
                      </>
                    ) : (
                      order.status !== 'completed' && (
                        <>
                          {order.status === 'pending' && (
                            <button type="button" disabled={busy} onClick={() => changeStatus(order, 'confirmed')}
                              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                              <FiClock size={15} /> Marquer préparée
                            </button>
                          )}
                          <button type="button" disabled={busy || order.paymentStatus !== 'completed'}
                            title={order.paymentStatus !== 'completed'
                              ? "Confirmez d'abord le paiement pour pouvoir terminer la commande"
                              : 'Marquer la commande comme terminée'}
                            onClick={() => changeStatus(order, 'completed')}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
                            <FiCheck size={15} /> Marquer terminée
                          </button>
                          <button type="button" disabled={busy}
                            onClick={() => { if (window.confirm('Annuler cette commande ? Le stock sera restauré.')) changeStatus(order, 'cancelled'); }}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 disabled:opacity-50">
                            <FiX size={15} /> Annuler
                          </button>
                        </>
                      )
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}

      {showCreate && (
        <CreateOrderModal scope="merchant" onClose={() => setShowCreate(false)} onCreated={fetchOrders} />
      )}

      {contactOrder && (
        <ContactCustomerModal order={contactOrder} onClose={() => setContactOrder(null)} />
      )}

      {shipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShipModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <FiTruck className="text-violet-600" />
              <h3 className="text-lg font-bold">Expédier la commande</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">Commande #{shipModal.order.orderNumber || (shipModal.order.id || shipModal.order._id)}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transporteur</label>
                <input value={shipForm.carrier} onChange={(e) => setShipForm((p) => ({ ...p, carrier: e.target.value }))}
                  placeholder="Ex. Amana, CTM, DHL…" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de suivi</label>
                <input value={shipForm.trackingNumber} onChange={(e) => setShipForm((p) => ({ ...p, trackingNumber: e.target.value }))}
                  placeholder="N° de suivi" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <p className="text-xs text-gray-400">Renseignez au moins l'un des deux champs.</p>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setShipModal(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50">Annuler</button>
              <button type="button"
                disabled={!shipForm.carrier.trim() && !shipForm.trackingNumber.trim()}
                onClick={() => doDelivery(shipModal.order, 'ship', { carrier: shipForm.carrier.trim(), trackingNumber: shipForm.trackingNumber.trim() })}
                className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed">
                Confirmer l'expédition
              </button>
            </div>
          </div>
        </div>
      )}

      {digitalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDigitalModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <FiDownloadCloud className="text-violet-600" />
              <h3 className="text-lg font-bold">Livrer le produit digital</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">Commande #{digitalModal.order.orderNumber || (digitalModal.order.id || digitalModal.order._id)}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lien d'accès *</label>
                <input value={digitalForm.link} onChange={(e) => setDigitalForm((p) => ({ ...p, link: e.target.value }))}
                  placeholder="https://… (lien de téléchargement, accès, clé…)" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note / instructions (facultatif)</label>
                <textarea value={digitalForm.note} onChange={(e) => setDigitalForm((p) => ({ ...p, note: e.target.value }))}
                  rows={3} placeholder="Identifiants, mode d'emploi, conditions…" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <p className="text-xs text-gray-400">L'acheteur est notifié et une fenêtre de traçabilité démarre. Tous les accès sont journalisés.</p>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setDigitalModal(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50">Annuler</button>
              <button type="button" disabled={!digitalForm.link.trim()}
                onClick={() => doDeliverDigital(digitalModal.order)}
                className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed">
                Remettre l'accès
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-center text-xs text-gray-400 mt-8">Maxula — contact@maxula.ma</p>
    </div>
  );
}
